/**
 * Bucket 3c — ephemeral handoff for "use this agent result as the active
 * operation's prompt". Mirrors `sourceHandoffStore` exactly: single-slot,
 * last-write-wins, not persisted.
 *
 * `AgentInvocationCard` pushes a result here when the user clicks "Use as
 * prompt"; the active operation panel subscribes reactively and seeds its
 * prompt field with the value (panel decides where it lands — Generate puts
 * it in `prompt`, ColorGrade puts it in `gradingStyle`, etc.).
 */

import { create } from 'zustand';

interface AgentPromptHandoff {
  /** Agent that produced the result — used by the consuming panel for a
   *  short attribution chip. */
  agentId: string;
  /** Display name of the producing agent (avoids re-fetching). */
  agentName: string;
  /** The text the user wants to push into the panel's prompt field. */
  promptText: string;
}

interface AgentResultStore {
  pending: AgentPromptHandoff | null;
  setHandoff: (handoff: AgentPromptHandoff) => void;
  consumeHandoff: () => AgentPromptHandoff | null;
  clearHandoff: () => void;
}

export const useAgentResultStore = create<AgentResultStore>((set, get) => ({
  pending: null,
  setHandoff: handoff => set({ pending: handoff }),
  consumeHandoff: () => {
    const pending = get().pending;
    if (pending !== null) set({ pending: null });
    return pending;
  },
  clearHandoff: () => set({ pending: null }),
}));
